import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { SerializationContext, SerializedData, DataModelBuilder, DataModel } from '../dto/data-model.js';
import { isSameData } from '../dto/data-model-utils.js';
import { Currency } from './currency.js';

export interface CurrencyExchangeEntry {
  from: Currency,
  to: Currency,
  rate: number,
}

export class CurrencyExchange implements ICloneable, IDataComparable {
  constructor(private readonly exchangeRate: CurrencyExchangeEntry[]) {
  }

  findExchangeRate(from: Currency, to: Currency): number | null {
    for(const item of this.exchangeRate) {
      if(item.from == from && item.to == to) {
        return item.rate;
      }
      else if(item.from == to && item.to == from) {
        return 1.0 / item.rate;
      }
    }

    return null;
  }

  static decodeOrNull(data: SerializedData[] | null): CurrencyExchange | null {
    return data != null ? this.decode(data) : null;
  }

  static decode(data: SerializedData[]): CurrencyExchange {
    return new CurrencyExchange(data.map((e) => ({
      from: toEnumFromString(MapUtils.get<string>(e, 'from'), Currency),
      to: toEnumFromString(MapUtils.get<string>(e, 'to'), Currency),
      rate: MapUtils.get<number>(e, 'rate'),
    })));
  }

  encode(): SerializedData[] {
    return this.exchangeRate.map((e) => ({
      'from': fromEnumAsString(e.from),
      'to': fromEnumAsString(e.to),
      'rate': e.rate,
    }));
  }

  clone(): this {
    const exchangeRate: CurrencyExchangeEntry[] = [];
    for(const item of this.exchangeRate) {
      exchangeRate.push({ ...item });
    }

    return new CurrencyExchange(exchangeRate) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    if(other.exchangeRate.length != this.exchangeRate.length) {
      return false;
    }

    for(let i = 0; i < this.exchangeRate.length; i++) {
      if(this.exchangeRate[i].from != other.exchangeRate[i].from ||
          this.exchangeRate[i].to != other.exchangeRate[i].to ||
          this.exchangeRate[i].rate != other.exchangeRate[i].rate) {
        return false;
      }
    }

    return true;
  }
}

export class CurrencySettings extends DataModel {
  static readonly TYPE_ID: string = '0d04b89fcb874f809c6a27d2ca51458c';

  constructor(
    public readonly displayCurrency: Currency,
    public readonly exchange: CurrencyExchange,
  ) {
    super();
  }

  get $typeId() {
    return CurrencySettings.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): CurrencySettings {
    return new CurrencySettings(
        toEnumFromString(MapUtils.get<string>(map, 'displayCurrency'), Currency),
        CurrencyExchange.decode(MapUtils.get<SerializedData[]>(map, 'exchange')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'displayCurrency': fromEnumAsString(this.displayCurrency),
      'exchange': this.exchange.encode(),
    };
  }

  clone(): this {
    return new CurrencySettings(
        this.displayCurrency,
        this.exchange.clone(),
    ) as this;
  }

  isSame(other: CurrencySettings): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.exchange, this.exchange) &&
        other.displayCurrency == this.displayCurrency;
  }
}

export class CurrencySettingsBuilder extends DataModelBuilder<CurrencySettings> {
  build(context: SerializationContext, map: SerializedData): CurrencySettings {
    return CurrencySettings.fromMap(context, map);
  }
}
